/**
 * 邮件详情查看器组件。
 *
 * 该组件是 CRM 邮件模块的核心展示组件，负责单封邮件的完整渲染和交互操作。
 * 主要功能包括：
 * - 邮件头信息展示：主题、发件人、收件人、抄送、回复地址、日期
 * - 邮件正文渲染：HTML 邮件通过 sandboxed iframe 安全渲染；纯文本邮件以 pre 标签展示
 * - 邮件标签管理：可添加/移除标签，支持标签下拉选择
 * - 邮件操作：删除、回复、回复全部、转发、拉黑发件人
 * - 附件下载：展示附件列表，点击下载
 * - 客户匹配：根据邮件收发地址自动匹配 CRM 中的客户，匹配到的地址可点击查看客户详情
 *
 * 安全设计：
 * - HTML 邮件经 DOMPurify 消毒后注入 sandboxed iframe，隔离脚本执行
 * - iframe 高度自适应内容，禁用滚动条
 *
 * 事件通信：
 * - 向上派发 `crm-email-reply`、`crm-email-reply-all`、`crm-email-forward`、`crm-quick-reply`、`crm-email-deleted` 事件
 * - 父组件监听这些事件完成实际的邮件操作（打开撰写界面等）
 *
 * 导出：`EmailViewer`（自定义元素 `email-viewer`）
 *
 * 依赖模块：`DOMPurify`（HTML 消毒）、`emailApi`/`emailBlacklistApi`/`customerApi`（数据 API）、`emailStore`（全局邮件状态）、`toast`/`showConfirm`（UI 反馈）、`formatFileSize`（工具函数）
 */
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Customer, Email, EmailAttachment, EmailTag } from '../types.js';
import { emailApi, emailBlacklistApi, customerApi } from '../api.js';
import { emailStore } from '../store.js';
import { BaseElement } from '../base-element.js';
import { toast } from './toast.js';
import { showConfirm } from './confirm-dialog.js';
import './customer-view-modal.js';
import { formatFileSize, sanitizeEmailHtml } from '../utils/index.js';

/**
 * 邮件详情查看器组件。
 *
 * 继承 `BaseElement`，通过 `@customElement('email-viewer')` 注册为自定义元素。
 * 父组件通过设置 `email` 属性传入邮件对象，组件自动拉取附件并匹配客户。
 *
 * 组件内部通过 `emailStore` 订阅标签列表的实时更新，保证标签操作后 UI 同步。
 */
@customElement('email-viewer')
export class EmailViewer extends BaseElement {
  /** 当前展示的邮件对象，null 时显示占位文案 */
  @property({ type: Object })
  email: Email | null = null;

  /** 标记当前邮件是否由当前用户发送（已发送邮件回复时收件人指向原收件人而非发件人） */
  @property({ type: Boolean }) isSentByMe = false;

  /** 是否隐藏邮件主题（当父组件已在上方显示主题时设为 true，避免重复） */
  @property({ type: Boolean }) hideSubject = false;

  /** 是否隐藏外层边框和圆角（嵌入到已有容器中时设为 true） */
  @property({ type: Boolean }) hideBorder = false;

  /** 邮件附件列表，在 email 属性变化时自动拉取 */
  @state()
  private attachments: EmailAttachment[] = [];

  /** 附件列表是否展开（附件数量超过折叠阈值时默认收起，仅显示摘要行） */
  @state()
  private attachmentsExpanded = false;

  /** 附件折叠阈值：附件数量超过该值时默认收起为摘要行，避免撑满详情区域 */
  private readonly attachmentCollapseThreshold = 6;

  /** 标签下拉菜单是否展开 */
  @state()
  private showTagDropdown = false;

  /** 全部可用标签列表，从 `emailStore` 订阅获取 */
  @state()
  private allTags: EmailTag[] = [];

  /** 邮箱地址到客户对象的映射，用于点击收发地址时跳转客户详情 */
  @state()
  private customerMap: Map<string, Customer> = new Map();

  /** 组件样式（当前为空，样式通过 Tailwind CSS 类名内联） */
  static styles = css``;

  /** emailStore 订阅取消函数，组件卸载时调用 */
  private _unsubscribe?: () => void;

  /** 生命周期：挂载时初始化标签列表、订阅 emailStore 变化、注册全局点击监听（用于关闭标签下拉） */
  connectedCallback() {
    super.connectedCallback();
    this.allTags = emailStore.state.tags;
    this._unsubscribe = emailStore.subscribe((state) => {
      this.allTags = state.tags;
    });
    document.addEventListener('click', this.handleClickOutside);
  }

  /** 生命周期：卸载时取消 emailStore 订阅、移除全局点击监听，防止内存泄漏 */
  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribe?.();
    document.removeEventListener('click', this.handleClickOutside);
  }

  /** 全局点击事件处理器：当标签下拉菜单展开时，任何外部点击都会关闭它 */
  private handleClickOutside = () => {
    if (this.showTagDropdown) this.showTagDropdown = false;
  };

  /**
   * 生命周期：属性变化后触发。
   *
   * 当 `email` 属性变化时（切换查看的邮件），重新拉取附件、匹配客户，
   * 并重置拉黑状态（`isBlocked` 设为 false）。
   *
   * @param changedProps - 变化的属性名到旧值的映射
   */
  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('email') && this.email) {
      this.fetchAttachments();
      this.matchCustomers();
      this.isBlocked = false;
      this.attachmentsExpanded = false;
    }
  }

  /**
   * 拉取当前邮件的附件列表。
   *
   * 若邮件无附件（attachment_count === 0），直接清空列表返回。
   * 请求失败时静默清空附件列表。
   */
  private async fetchAttachments() {
    if (!this.email || this.email.attachment_count === 0) {
      this.attachments = [];
      return;
    }
    try {
      const resp = await emailApi.getAttachments(this.email.id);
      this.attachments = resp.data;
    } catch { this.attachments = []; }
  }

  /** 附件总大小（字节），用于折叠摘要行展示 */
  private get totalAttachmentSize(): number {
    return this.attachments.reduce((sum, att) => sum + (att.file_size || 0), 0);
  }

  /** 切换附件列表的展开/收起状态（仅附件数量超过折叠阈值时可点击） */
  private toggleAttachments() {
    this.attachmentsExpanded = !this.attachmentsExpanded;
  }

  /**
   * 根据邮件的发件人、收件人、抄送地址批量匹配 CRM 客户。
   *
   * 收集所有邮箱地址后调用 `customerApi.matchByEmails` 批量查询，
   * 将匹配结果存入 `customerMap`（key 为小写邮箱地址，value 为客户对象）。
   * 匹配到的地址在渲染时会变为可点击链接，点击后弹出客户详情弹窗。
   */
  private async matchCustomers() {
    if (!this.email) return;
    // 收集所有邮箱地址（发件人 + 收件人 + 抄送），统一转小写
    const emails: string[] = [];
    if (this.email.send_from?.email) emails.push(this.email.send_from.email.toLowerCase());
    (this.email.send_to || []).forEach(a => { if (a.email) emails.push(a.email.toLowerCase()); });
    (this.email.cc || []).forEach(a => { if (a.email) emails.push(a.email.toLowerCase()); });
    if (emails.length === 0) { this.customerMap = new Map(); return; }
    try {
      const matches = await customerApi.matchByEmails(emails);
      // 构建邮箱地址 → 客户的映射表
      this.customerMap = new Map(matches.map(m => [m.email.toLowerCase(), m.customer]));
    } catch { this.customerMap = new Map(); }
  }

  /**
   * 处理收发地址点击事件。
   *
   * 当点击的地址在 customerMap 中有匹配的客户时，打开客户详情弹窗。
   * 通过 shadowRoot 查找 `#customerViewModal` 元素并调用其 `show()` 方法。
   *
   * @param e - 点击事件对象，用于阻止冒泡
   * @param emailAddr - 被点击的邮箱地址
   */
  private handleRecipientClick(e: Event, emailAddr: string) {
    e.stopPropagation();
    const customer = this.customerMap.get(emailAddr.toLowerCase());
    if (!customer) return;
    const modal = this.shadowRoot?.querySelector('#customerViewModal') as any;
    if (modal) modal.show(customer);
  }

  /**
   * 渲染邮件地址列表（发件人、收件人、抄送等）。
   *
   * 每个地址以 "名称 <邮箱>" 格式展示。若该地址在 customerMap 中匹配到客户，
   * 则渲染为可点击的蓝色链接（点击触发 `handleRecipientClick`）。
   * 多个地址以逗号分隔。
   *
   * @param addresses - 地址数组，每项含可选 name 和必填 email
   * @returns Lit 模板片段数组
   */
  private renderAddresses(addresses: Array<{ name?: string; email: string }>) {
    if (!addresses || addresses.length === 0) return html`未知`;
    const parts: any[] = [];
    addresses.forEach((a, i) => {
      const matched = this.customerMap.has(a.email?.toLowerCase());
      const display = a.name ? `${a.name} <${a.email}>` : a.email;
      if (matched) {
        parts.push(html`<span class="text-info font-medium cursor-pointer hover:underline" @click=${(e: Event) => this.handleRecipientClick(e, a.email)}>${display}</span>`);
      } else {
        parts.push(html`<span>${display}</span>`);
      }
      if (i < addresses.length - 1) parts.push(html`, `);
    });
    return parts;
  }

  /**
   * iframe 加载完成后的自适应高度处理。
   *
   * 读取 iframe 内部文档的 body.scrollHeight，动态设置 iframe 高度，
   * 使其完全展示邮件内容而无需内部滚动条。
   * 最小高度 200px，额外加 20px 避免边距截断。
   * 跨域时 contentDocument 访问会抛异常，静默忽略。
   *
   * @param e - iframe 的 load 事件对象
   */
  private handleIframeLoad(e: Event) {
    const iframe = e.target as HTMLIFrameElement;
    try {
      const doc = iframe.contentDocument;
      if (doc?.body) iframe.style.height = Math.max(200, doc.body.scrollHeight + 20) + 'px';
    } catch {
      // 跨域 iframe 无法读取 contentDocument，保留浏览器默认高度。
    }
  }

  /**
   * 下载邮件附件。
   *
   * 调用 `emailApi.downloadAttachment` 获取 Blob 数据，通过临时 <a> 标签触发浏览器下载。
   * 下载完成后立即释放 ObjectURL 防止内存泄漏。
   * 失败时打印错误日志。
   *
   * @param att - 要下载的附件对象
   */
  private async downloadAttachment(att: EmailAttachment) {
    try {
      const blob = await emailApi.downloadAttachment(att.email_id, att.id);
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = att.filename;
        a.click();
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Download failed:', err);
    }
  }

  /**
   * 切换邮件的某个标签（添加或移除）。
   *
   * 若当前邮件已包含该标签则移除，否则添加。
   * 通过 `emailStore.setEmailTags` 更新后端并同步本地 email 对象的 tags 字段。
   *
   * @param tagId - 要切换的标签 ID
   */
  private async toggleTag(tagId: number) {
    if (!this.email) return;
    const currentIds = (this.email.tags || []).map(t => t.id);
    // 若已包含该标签则移除（filter），否则添加（spread + push）
    const newIds = currentIds.includes(tagId)
      ? currentIds.filter(id => id !== tagId)
      : [...currentIds, tagId];
    await emailStore.setEmailTags(this.email.id, newIds);
    // 同步更新本地 email 对象的 tags 字段，从 allTags 中筛选当前关联的标签
    this.email = { ...this.email, tags: this.allTags.filter(t => newIds.includes(t.id)) };
    this.requestUpdate();
  }

  /** 当前发件人是否已被拉黑（影响拉黑按钮的显示状态） */
  @state() private isBlocked = false;
  /** 拉黑操作进行中状态（防止重复点击） */
  @state() private blocking = false;

  /**
   * 触发"回复"事件。
   *
   * 向上派发 `crm-email-reply` 自定义事件，携带当前邮件对象。
   * 父组件监听后打开邮件撰写界面，预填收件人为原邮件发件人。
   */
  private handleReply() {
    if (!this.email) return;
    this.dispatchEvent(new CustomEvent('crm-email-reply', {
      detail: this.email, bubbles: true, composed: true,
    }));
  }

  /**
   * 触发"回复全部"事件。
   *
   * 向上派发 `crm-email-reply-all` 自定义事件，携带当前邮件对象。
   * 父组件监听后打开撰写界面，预填收件人为原发件人 + 所有收件人 + 抄送人。
   */
  private handleReplyAll() {
    if (!this.email) return;
    this.dispatchEvent(new CustomEvent('crm-email-reply-all', {
      detail: this.email, bubbles: true, composed: true,
    }));
  }

  /**
   * 触发"转发"事件。
   *
   * 向上派发 `crm-email-forward` 自定义事件，携带当前邮件对象。
   * 父组件监听后打开撰写界面，将原邮件作为附件或引用内容转发。
   */
  private handleForward() {
    if (!this.email) return;
    this.dispatchEvent(new CustomEvent('crm-email-forward', {
      detail: this.email, bubbles: true, composed: true,
    }));
  }

  /**
   * 拉黑当前邮件的发件人。
   *
   * 将发件人邮箱加入黑名单，成功后显示 block toast 提示。
   * 若该地址已在黑名单中（后端返回特定错误消息），同样标记为已拉黑状态。
   * 使用 `blocking` 状态防止操作进行中重复点击。
   */
  private async handleBlockSender() {
    if (!this.email || this.blocking) return;
    const addr = this.email.send_from?.email;
    if (!addr) return;
    this.blocking = true;
    try {
      await emailBlacklistApi.add(addr);
      this.isBlocked = true;
      toast.block(`${addr} 已拉黑`);
    } catch (err: any) {
      if (err.message?.includes('已在黑名单中')) {
        this.isBlocked = true;
        toast.info(`${addr} 已在黑名单中`);
      } else {
        toast.error(err.message || '操作失败');
      }
    } finally {
      this.blocking = false;
    }
  }

  /**
   * 删除当前邮件。
   *
   * 先弹出确认对话框，用户确认后通过 `emailStore.deleteEmail` 执行删除。
   * 删除成功后向上派发 `crm-email-deleted` 事件，通知父组件更新邮件列表。
   * 失败时显示错误 toast。
   */
  private async handleDeleteEmail() {
    if (!this.email) return;
    if (!(await showConfirm('确定要删除这封邮件吗？'))) return;
    try {
      await emailStore.deleteEmail(this.email.id);
      this.dispatchEvent(new CustomEvent('crm-email-deleted', { detail: { emailId: this.email.id }, bubbles: true, composed: true }));
    } catch (err: any) {
      toast.error(err.message || '删除失败');
    }
  }

  /**
   * 主渲染方法。
   *
   * 组件布局自上而下分为：
   * 1. 邮件头区域（bg-bg）：主题（可通过 hideSubject 隐藏）+ 操作按钮（删除/标签/拉黑/回复/转发）+ 标签展示 + 发收件人信息
   * 2. 邮件正文区域：HTML 邮件用 sandboxed iframe 渲染，纯文本用 pre 标签
   * 3. 附件区域（条件渲染）：附件列表，点击下载
   *
   * 已发送邮件（isSentByMe）显示"已发送"徽标和"回复全部/回复/转发"按钮，
   * 回复自己发出的邮件时，父组件会把收件人指向原收件人列表。
   *
   * @returns Lit 模板结果
   */
  render() {
    if (!this.email) {
      return html`<div class="text-center py-12 text-text-muted">选择邮件查看详情</div>`;
    }
    const e = this.email;
    const subject = e.subject || '无主题';
    // 优先使用邮件日期，回退到创建时间
    const displayDate = e.email_date || (e as any).created_at;
    const formattedDate = displayDate ? new Date(displayDate * 1000).toLocaleString('zh-CN') : '';
    const hasCC = e.cc && e.cc.length > 0;
    const hasReplyTo = e.reply_to?.email && e.reply_to.email !== '';
    const hasHTML = !!e.html_content;
    const hasAttachments = this.attachments.length > 0;
    // 附件数量超过阈值时默认收起为摘要行，点击后展开
    const attachmentsCollapsible = this.attachments.length > this.attachmentCollapseThreshold;
    const showAttachmentList = hasAttachments && (!attachmentsCollapsible || this.attachmentsExpanded);

    return html`
      <div class="bg-surface flex flex-col h-full ${this.hideBorder ? '' : 'rounded-md border border-border shadow-sm'}">
        <!-- 操作按钮行：白底独立行，脱离下方灰底信息区 -->
        <div class="flex items-center gap-2 p-3 border-b border-border flex-shrink-0">
            <button class="px-3 py-1.5 text-xs font-medium text-white bg-error hover:bg-error-hover rounded transition-colors cursor-pointer flex items-center gap-1 shadow-sm shadow-error/20"
                    @click=${this.handleDeleteEmail}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              删除
            </button>
            <div class="relative">
              <button class="px-3 py-1.5 text-xs font-medium text-primary border border-primary/30 hover:bg-primary/5 rounded transition-colors cursor-pointer flex items-center gap-1"
                      @click=${(e: Event) => { e.stopPropagation(); this.showTagDropdown = !this.showTagDropdown; }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
                标签
              </button>
              ${this.showTagDropdown ? html`
                <div class="absolute right-0 top-full mt-1 bg-surface border border-border rounded-md shadow-xl z-50 min-w-[160px] max-h-48 overflow-y-auto py-1" @click=${(e: Event) => e.stopPropagation()}>
                  ${this.allTags.length === 0 ? html`
                    <div class="px-3 py-2 text-xs text-text-muted">暂无标签，请先创建</div>
                  ` : this.allTags.map(tag => {
                    const assigned = (e.tags || []).some(t => t.id === tag.id);
                    return html`
                      <div class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-bg text-text"
                           @click=${() => { this.toggleTag(tag.id); this.showTagDropdown = false; }}>
                        <span class="w-2.5 h-2.5 rounded-full flex-shrink-0" style="background:${tag.color}"></span>
                        <span class="flex-1">${tag.name}</span>
                        ${assigned ? html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 text-primary"><path d="M20 6 9 17l-5-5"/></svg>` : ''}
                      </div>`;
                  })}
                </div>
              ` : ''}
            </div>
            ${!this.isBlocked ? html`
              <button class="px-3 py-1.5 text-xs font-medium text-error border border-error/30 hover:bg-error/5 rounded transition-colors cursor-pointer flex items-center gap-1"
                      @click=${this.handleBlockSender} ?disabled=${this.blocking}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                ${this.blocking ? '处理中...' : '拉黑'}
              </button>
            ` : html`
              <span class="px-3 py-1.5 text-xs font-medium text-text-muted bg-bg border border-border rounded-md flex items-center gap-1">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                已拉黑
              </span>
            `}
            ${this.isSentByMe ? html`
              <span class="px-2 py-1 text-xs font-medium text-text-muted bg-bg border border-border rounded-md">
                已发送
              </span>
              <button class="px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20 flex items-center gap-1"
                      @click=${this.handleReplyAll}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M21 9a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                回复全部
              </button>
              <button class="px-3 py-1.5 text-xs font-medium text-white bg-info hover:opacity-90 rounded transition-colors cursor-pointer shadow-sm shadow-info/20 flex items-center gap-1"
                      @click=${this.handleReply}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                回复
              </button>
              <button class="px-3 py-1.5 text-xs font-medium text-white bg-success hover:bg-success-hover rounded transition-colors cursor-pointer shadow-sm shadow-success/20 flex items-center gap-1"
                      @click=${this.handleForward}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><path d="m17 2 4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                转发
              </button>
            ` : html`
              <button class="px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-hover rounded transition-colors cursor-pointer shadow-sm shadow-primary/20 flex items-center gap-1"
                      @click=${this.handleReplyAll}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/><path d="M21 9a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                回复全部
              </button>
              <button class="px-3 py-1.5 text-xs font-medium text-white bg-info hover:opacity-90 rounded transition-colors cursor-pointer shadow-sm shadow-info/20 flex items-center gap-1"
                      @click=${this.handleReply}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                回复
              </button>
              <button class="px-3 py-1.5 text-xs font-medium text-white bg-success hover:bg-success-hover rounded transition-colors cursor-pointer shadow-sm shadow-success/20 flex items-center gap-1"
                      @click=${this.handleForward}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><path d="m17 2 4 4-4 4"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><path d="m7 22-4-4 4-4"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                转发
              </button>
            `}
        </div>
        <!-- 邮件头信息区：灰底 -->
        <div class="bg-bg p-3 border-b border-border flex-shrink-0">
          <!-- 邮件主题（可通过 hideSubject 隐藏） -->
          ${this.hideSubject ? '' : html`<h2 class="text-lg font-semibold m-0 mb-2 text-text">${subject}</h2>`}
          <div class="flex items-center gap-1.5 flex-wrap mb-2">
            ${(e.tags || []).map(tag => html`
              <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity" style="background:${tag.color}20;color:${tag.color};border:1px solid ${tag.color}40"
                    @click=${() => this.toggleTag(tag.id)} title="点击移除">
                ${tag.name} <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3 h-3"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </span>
            `)}
          </div>
          <div class="grid gap-2 text-sm">
            <div class="flex gap-2">
              <span class="font-medium text-text-muted shrink-0 inline-block w-14 text-right">发件人:</span>
              <span class="text-text">${this.renderAddresses(e.send_from?.email ? [e.send_from] : [])}</span>
            </div>
            <div class="flex gap-2">
              <span class="font-medium text-text-muted shrink-0 inline-block w-14 text-right">收件人:</span>
              <span class="text-text">${this.renderAddresses(e.send_to || [])}</span>
            </div>
            ${hasCC ? html`<div class="flex gap-2">
              <span class="font-medium text-text-muted shrink-0 inline-block w-14 text-right">抄送:</span>
              <span class="text-text">${this.renderAddresses(e.cc || [])}</span>
            </div>` : ''}
            ${hasReplyTo ? html`<div class="flex gap-2">
              <span class="font-medium text-text-muted shrink-0 inline-block w-14 text-right">回复:</span>
              <span class="text-text">${e.reply_to.name ? `${e.reply_to.name} <${e.reply_to.email}>` : e.reply_to.email}</span>
            </div>` : ''}
            ${formattedDate ? html`<div class="flex gap-2">
              <span class="font-medium text-text-muted shrink-0 inline-block w-14 text-right">日期:</span>
              <span class="text-text">${formattedDate}</span>
            </div>` : ''}
          </div>
        </div>
        <div class="p-3 flex-1 min-h-0 overflow-y-auto">
          ${hasHTML
            ? html`<iframe srcdoc=${sanitizeEmailHtml(e.html_content)} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox" style="width:100%;border:none;min-height:200px;overflow:hidden;" scrolling="no" @load=${this.handleIframeLoad}></iframe>`
            : html`<div class="bg-bg rounded-md p-4"><pre class="whitespace-pre-wrap break-words m-0 text-sm text-text leading-relaxed" style="font-family:var(--font-sans);">${e.content || '无内容'}</pre></div>`}
        </div>
        <!-- 附件区域：仅在有附件时渲染；数量超阈值时折叠为摘要行，展开后内部滚动 -->
        ${hasAttachments ? html`
          <div class="p-3 border-t border-border bg-bg flex-shrink-0">
            <div class="text-sm font-medium m-0 ${showAttachmentList ? 'mb-3' : ''} text-text flex items-center gap-2 ${attachmentsCollapsible ? 'cursor-pointer select-none hover:text-primary transition-colors' : ''}"
                 @click=${attachmentsCollapsible ? this.toggleAttachments : null}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0"><path d="m21.44 11.58-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 9.34l-8.54 8.54a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
              附件 (${this.attachments.length})
              ${attachmentsCollapsible ? html`
                <span class="text-text-muted font-normal text-xs">共 ${formatFileSize(this.totalAttachmentSize)}${this.attachmentsExpanded ? '' : ' · 点击查看'}</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 ml-auto transition-transform ${this.attachmentsExpanded ? 'rotate-180' : ''}"><path d="m6 9 6 6 6-6"/></svg>
              ` : ''}
            </div>
            ${showAttachmentList ? html`
              <div class="flex flex-wrap gap-2 ${attachmentsCollapsible ? 'max-h-[104px] overflow-y-auto' : ''}">
                ${this.attachments.map(att => html`
                  <div @click=${() => this.downloadAttachment(att)} title=${att.filename} class="flex items-center gap-2 px-3 py-2 bg-surface border border-border rounded-md text-xs text-text hover:bg-bg transition-colors cursor-pointer max-w-full">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 text-text-muted shrink-0"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4h4"/></svg>
                    <span class="font-medium truncate max-w-[240px] inline-block">${att.filename}</span>
                    <span class="text-text-muted shrink-0">(${formatFileSize(att.file_size)})</span>
                  </div>
                `)}
              </div>
            ` : ''}
          </div>
        ` : ''}
        <!-- Customer View Modal: 用于点击收发地址时弹出客户详情 -->
        <crm-customer-view-modal id="customerViewModal"></crm-customer-view-modal>
      </div>
    `;
  }
}
